import React, { useEffect, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import { ICampaignContextProps } from '@context/Campaign'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { EventTrackingNames } from '@enums'
import { withCampaign, withSubmissionListFilter } from '@hocs'
import { usePrevious } from '@hooks/usePrevious'
import { IApplicationState } from '@store'
import { selectIsAddingFeedback } from '@store/feedback'
import { bulkAddFeedback } from '@store/feedback/actions/bulkAdd'
import {
  Button,
  CloseIcon,
  Form,
  IWithFormValidationProps,
  Modal,
  ModalTitle,
  TextArea,
} from '@tribegroup'
import { isEmpty, toEventsTracking } from '@utils'
import { FeedbackActionButtonsWrapper } from './BulkFeedback.styled'

export interface IInboxTitleBarSelectionModeBulkFeedbackProps {
  submissionIds: ReadonlyArray<number>
  campaignId: number
  isOpen?: boolean
  onCancel: VoidFunction
}

interface IProps
  extends IInboxTitleBarSelectionModeBulkFeedbackProps,
    ICampaignContextProps,
    IWithFormValidationProps,
    ISubmissionListFilterContextProps {}

const messageCharacterLimit = 800

export const InboxTitleBarSelectionModeBulkFeedback: React.FC<IProps> = ({
  submissionIds,
  isOpen = false,
  onCancel,
  campaign,
  filter,
  sortBy,
  validate,
  setForm,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const isAddingFeedback = useSelector((state: IApplicationState) => selectIsAddingFeedback(state))

  const [message, setMessage] = useState('')

  const prevIsAddingFeedback = usePrevious(isAddingFeedback)

  useEffect(() => {
    if (prevIsAddingFeedback && !isAddingFeedback) {
      onCancel()
    }
  }, [isAddingFeedback])

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const trimmedMessage = message.trim()

    const analytics = toEventsTracking(EventTrackingNames.SubmitBulkFeedback, {
      brief_id: campaign.id,
      role: campaign.role,
      count: submissionIds.length,
      current_filter: filter,
      message: trimmedMessage,
      brief_state: campaign.status,
    })

    if (trimmedMessage.length > 0 && trimmedMessage.length <= messageCharacterLimit) {
      dispatch(
        bulkAddFeedback(
          campaign.id,
          {
            message_body: trimmedMessage,
            submission_ids: submissionIds,
          },
          {
            refreshSubmissions: true,
            filter,
            sortBy,
            event: analytics,
          },
        ),
      )
    }
  }

  const onCancelClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    onCancel()
  }

  const onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.currentTarget.value)
  }

  return (
    <Modal reactModalProps={{ isOpen, onRequestClose: onCancel }} width={50} complex>
      <ModalTitle center>
        <FormattedMessage
          id="submission.feedback.form.bulk.title"
          values={{ value: submissionIds && submissionIds.length }}
        />
      </ModalTitle>
      <CloseIcon onClick={onCancel} />
      <Form onSubmit={onSubmit} onBeforeSubmit={validate} setForm={setForm} method="POST">
        <TextArea
          height={8}
          placeholder={intl.formatMessage({
            id: 'submission.feedback.form.bulk.message.placeholder',
          })}
          value={message}
          onChange={onMessageChange}
          characterLimit={messageCharacterLimit}
        />
        <FeedbackActionButtonsWrapper>
          <Flex justifyEnd>
            <Button
              scheme="primary"
              outlined
              onClick={onCancelClick}
              fontSize={0.875}
              height={3}
              width={10.125}
            >
              <FormattedMessage id="core.text.cancel" />
            </Button>
            <Button
              scheme="primary"
              fontSize={0.875}
              height={3}
              width={10.125}
              leftOuterSpacing={1}
              disabled={isAddingFeedback || isEmpty(message.trim())}
              loading={isAddingFeedback}
            >
              <FormattedMessage id="core.text.submit" />
            </Button>
          </Flex>
        </FeedbackActionButtonsWrapper>
      </Form>
    </Modal>
  )
}

export default withCampaign(withSubmissionListFilter(InboxTitleBarSelectionModeBulkFeedback))
